import { useEffect, useState } from "react";
import { LogOut, Save, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "./ui/switch";
import { api, authApi } from "../lib/api";
import { AddCourtForm, AddCoachForm } from "./AdminAddForms";

const tabs = ["Courts", "Coaches", "Settings", "Activity"];

function EditRow({ item, fields, onSave, onDelete, testPrefix }) {
  const [draft, setDraft] = useState(item);
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    await onSave(item.id, draft);
    setSaving(false);
  };
  return (
    <div className="bg-[#0f0f10] border border-[#2a2a2a] rounded-md p-6" data-testid={`${testPrefix}-row-${item.id}`}>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {fields.map((f) => (
          <label key={f.key} className={`flex flex-col gap-1.5 ${f.wide ? "sm:col-span-2 lg:col-span-4" : ""}`}>
            <span className="text-[11px] uppercase tracking-widest text-white/40">{f.label}</span>
            {f.wide ? (
              <textarea
                rows={2}
                value={draft[f.key] ?? ""}
                onChange={(e) => setDraft({ ...draft, [f.key]: e.target.value })}
                data-testid={`${testPrefix}-${item.id}-${f.key}-input`}
                className="bg-[#1a1a1a] border border-white/15 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-[#ccff00]/60"
              />
            ) : (
              <input
                type={f.type || "text"}
                value={draft[f.key] ?? ""}
                onChange={(e) =>
                  setDraft({ ...draft, [f.key]: f.type === "number" ? Number(e.target.value) : e.target.value })
                }
                data-testid={`${testPrefix}-${item.id}-${f.key}-input`}
                className="bg-[#1a1a1a] border border-white/15 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-[#ccff00]/60"
              />
            )}
          </label>
        ))}
      </div>
      <div className="mt-5 flex items-center gap-3">
        <button
          onClick={save}
          disabled={saving}
          data-testid={`${testPrefix}-${item.id}-save-btn`}
          className="bg-[#ccff00] text-black font-bold uppercase tracking-wider text-xs px-6 py-2.5 rounded-full hover:bg-[#a3e635] transition-colors disabled:opacity-50 inline-flex items-center gap-2"
        >
          <Save size={14} /> {saving ? "Saving…" : "Save"}
        </button>
        <button
          onClick={() => {
            if (window.confirm(`Delete "${item.name}"? This cannot be undone.`)) onDelete(item.id);
          }}
          data-testid={`${testPrefix}-${item.id}-delete-btn`}
          className="border border-red-500/40 text-red-400 uppercase tracking-wider text-xs font-semibold px-5 py-2.5 rounded-full hover:bg-red-500/10 transition-colors inline-flex items-center gap-2"
        >
          <Trash2 size={13} /> Delete
        </button>
      </div>
    </div>
  );
}

export const AdminDashboard = ({ user, onLogout }) => {
  const [tab, setTab] = useState("Courts");
  const [courts, setCourts] = useState([]);
  const [coaches, setCoaches] = useState([]);
  const [settings, setSettings] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [memberships, setMemberships] = useState([]);

  useEffect(() => {
    api.get("/courts").then((r) => setCourts(r.data)).catch(() => {});
    api.get("/coaches").then((r) => setCoaches(r.data)).catch(() => {});
    authApi.get("/admin/settings").then((r) => setSettings(r.data)).catch(() => {});
    authApi.get("/admin/bookings").then((r) => setBookings(r.data)).catch(() => {});
    authApi.get("/admin/memberships").then((r) => setMemberships(r.data)).catch(() => {});
  }, []);

  const saveCourt = async (id, draft) => {
    try {
      const { name, price, tagline, description, surface, type } = draft;
      const r = await authApi.put(`/admin/courts/${id}`, { name, price, tagline, description, surface, type });
      setCourts((prev) => prev.map((c) => (c.id === id ? r.data : c)));
      toast.success(`${r.data.name} updated`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    }
  };

  const saveCoach = async (id, draft) => {
    try {
      const { name, price, role, specialty, bio } = draft;
      const r = await authApi.put(`/admin/coaches/${id}`, { name, price, role, specialty, bio });
      setCoaches((prev) => prev.map((c) => (c.id === id ? r.data : c)));
      toast.success(`${r.data.name} updated`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    }
  };

  const toggleConcierge = async (checked) => {
    try {
      const r = await authApi.put("/admin/settings", { concierge_mode: checked });
      setSettings(r.data);
      toast.success(checked ? "Concierge mode ON — Ace sticks to tennis" : "Universal mode ON — Ace answers anything");
    } catch {
      toast.error("Could not update setting");
    }
  };

  const addCourt = async (payload) => {
    try {
      const r = await authApi.post("/admin/courts", payload);
      setCourts((prev) => [...prev, r.data]);
      toast.success(`${r.data.name} added — it's live on the Courts page`);
      return true;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not add court");
      return false;
    }
  };

  const deleteCourt = async (id) => {
    try {
      await authApi.delete(`/admin/courts/${id}`);
      setCourts((prev) => prev.filter((c) => c.id !== id));
      toast.success("Court deleted");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not delete court");
    }
  };

  const addCoach = async (payload) => {
    try {
      const r = await authApi.post("/admin/coaches", payload);
      setCoaches((prev) => [...prev, r.data]);
      toast.success(`${r.data.name} added to the coaching team`);
      return true;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not add coach");
      return false;
    }
  };

  const deleteCoach = async (id) => {
    try {
      await authApi.delete(`/admin/coaches/${id}`);
      setCoaches((prev) => prev.filter((c) => c.id !== id));
      toast.success("Coach deleted");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not delete coach");
    }
  };

  return (
    <main className="max-w-7xl mx-auto px-6 lg:px-10 py-16" data-testid="admin-dashboard">
      <div className="flex flex-wrap items-start justify-between gap-6 mb-12">
        <div>
          <p className="text-[#ccff00] uppercase tracking-[0.3em] text-sm mb-3">Admin</p>
          <h1 className="font-display uppercase text-4xl sm:text-5xl tracking-tight">Club Control Room</h1>
          <p className="text-white/50 mt-3">Signed in as {user.email}</p>
        </div>
        <button
          onClick={onLogout}
          data-testid="admin-dashboard-logout-btn"
          className="border border-white/25 text-white uppercase tracking-wider text-sm font-semibold px-6 py-2.5 rounded-full hover:border-[#ccff00]/70 hover:text-[#ccff00] transition-colors inline-flex items-center gap-2"
        >
          <LogOut size={14} /> Sign out
        </button>
      </div>

      <div className="flex gap-2 flex-wrap mb-10">
        {tabs.map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            data-testid={`admin-tab-${t.toLowerCase()}`}
            className={`px-6 py-2.5 rounded-full text-sm uppercase tracking-widest transition-colors ${
              tab === t ? "bg-[#ccff00] text-black font-bold" : "border border-white/15 text-white/60 hover:text-white"
            }`}
          >
            {t}
          </button>
        ))}
      </div>

      {tab === "Courts" && (
        <div className="space-y-6" data-testid="admin-courts-panel">
          <AddCourtForm onAdd={addCourt} />
          {courts.map((c) => (
            <EditRow
              key={c.id}
              item={c}
              testPrefix="admin-court"
              onSave={saveCourt}
              onDelete={deleteCourt}
              fields={[
                { key: "name", label: "Name" },
                { key: "price", label: "Price ($/hr)", type: "number" },
                { key: "surface", label: "Surface" },
                { key: "type", label: "Type" },
                { key: "tagline", label: "Tagline", wide: true },
              ]}
            />
          ))}
        </div>
      )}

      {tab === "Coaches" && (
        <div className="space-y-6" data-testid="admin-coaches-panel">
          <AddCoachForm onAdd={addCoach} />
          {coaches.map((c) => (
            <EditRow
              key={c.id}
              item={c}
              testPrefix="admin-coach"
              onSave={saveCoach}
              onDelete={deleteCoach}
              fields={[
                { key: "name", label: "Name" },
                { key: "price", label: "Price ($/hr)", type: "number" },
                { key: "role", label: "Role" },
                { key: "specialty", label: "Specialty" },
                { key: "bio", label: "Bio", wide: true },
              ]}
            />
          ))}
        </div>
      )}

      {tab === "Settings" && settings && (
        <div className="bg-[#0f0f10] border border-[#2a2a2a] rounded-md p-8 max-w-2xl" data-testid="admin-settings-panel">
          <div className="flex items-center justify-between gap-6">
            <div>
              <h3 className="font-display uppercase text-xl tracking-tight">Tennis Concierge Mode</h3>
              <p className="text-white/60 mt-2 leading-relaxed">
                When ON, Ace politely declines off-topic questions and steers everything back to tennis and the club — cutting AI costs. When OFF, Ace answers anything (universal mode).
              </p>
            </div>
            <Switch
              checked={settings.concierge_mode}
              onCheckedChange={toggleConcierge}
              data-testid="admin-concierge-toggle"
              aria-label="Toggle tennis concierge mode"
              className="data-[state=checked]:bg-[#ccff00]"
            />
          </div>
          <p className="mt-6 text-sm">
            Current mode:{" "}
            <span className="text-[#ccff00] font-bold uppercase tracking-widest" data-testid="admin-concierge-status">
              {settings.concierge_mode ? "Concierge" : "Universal"}
            </span>
          </p>
        </div>
      )}

      {tab === "Activity" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8" data-testid="admin-activity-panel">
          <div className="bg-[#0f0f10] border border-[#2a2a2a] rounded-md p-6">
            <h3 className="font-display uppercase text-xl tracking-tight mb-4">Recent Bookings ({bookings.length})</h3>
            <div className="space-y-3 max-h-[420px] overflow-y-auto">
              {bookings.map((b) => (
                <div key={b.id} className="border-b border-white/10 pb-3 text-sm">
                  <p className="text-[#ccff00] font-mono">{b.ref}</p>
                  <p className="text-white/70">{b.court_name} · {b.date} {b.time} · {b.name}</p>
                </div>
              ))}
              {bookings.length === 0 && <p className="text-white/40 text-sm">No bookings yet.</p>}
            </div>
          </div>
          <div className="bg-[#0f0f10] border border-[#2a2a2a] rounded-md p-6">
            <h3 className="font-display uppercase text-xl tracking-tight mb-4">Recent Memberships ({memberships.length})</h3>
            <div className="space-y-3 max-h-[420px] overflow-y-auto">
              {memberships.map((m) => (
                <div key={m.id} className="border-b border-white/10 pb-3 text-sm">
                  <p className="text-[#ccff00] font-mono">{m.ref}</p>
                  <p className="text-white/70">{m.tier_name} · ${m.price}/mo · {m.name}</p>
                </div>
              ))}
              {memberships.length === 0 && <p className="text-white/40 text-sm">No memberships yet.</p>}
            </div>
          </div>
        </div>
      )}
    </main>
  );
};

export default AdminDashboard;
